import React from 'react';
import { type ActiveSidebarContext } from './IconBar';
import { type Language, useLocalization } from '../../config/localization';
import { useSidebar } from '../../contexts/SidebarContext';
import Icon, { ICONS } from '../ui/icons';
import { SignInButton, SignUpButton } from '@clerk/clerk-react';
import OrganizationContext from './contexts/OrganizationContext';
import ImageGenerationContext from './contexts/ImageGenerationContext';
import MultiContext from './contexts/MultiContext';
import GalleryFiltersContext from './contexts/GalleryFiltersContext';
import SettingsContext from './contexts/SettingsContext';
import HelpContext from './contexts/HelpContext';
import AdminContext from './contexts/AdminContext';
import type { GalleryFilters } from '../pages/gallery/GalleryContent';

interface ContextPanelProps {
  activeContext: ActiveSidebarContext;
  selectedLanguage: Language;
  onLanguageChange: (language: Language) => void;
  className?: string;
  isUnauthenticated?: boolean;
  // Gallery filter props
  galleryFilters?: GalleryFilters;
  onGalleryFiltersChange?: (filters: Partial<GalleryFilters>) => void;
  // Workflow tile selector modal opener
  onOpenTileSelector?: (workflows: any[], selectedId: string | null, onSelect: (id: string) => void) => void;
  // Multi section: model picker supplied by MultiGenerationPage (owns the state)
  multiSidebarPanel?: React.ReactNode;
  // Multi section: cost + Generate, pinned to the bottom of the panel
  multiSidebarFooter?: React.ReactNode;
}

export default function ContextPanel({
  activeContext,
  selectedLanguage,
  onLanguageChange,
  className = '',
  isUnauthenticated = false,
  galleryFilters,
  onGalleryFiltersChange,
  onOpenTileSelector,
  multiSidebarPanel,
  multiSidebarFooter
}: ContextPanelProps) {
  const { isContextPanelCollapsed, toggleContextPanel } = useSidebar();
  const t = useLocalization(selectedLanguage);
  
  // Component for unauthenticated users showing login prompt
  const UnauthenticatedMessage = () => (
    <div className="h-full flex flex-col items-center justify-center p-6 text-center">
      <div className="text-6xl mb-4">🔐</div>
      <h3 className="text-lg font-semibold text-white mb-2">
        {t.auth?.required || 'Требуется авторизация'}
      </h3>
      <p className="text-sm text-gray-400 mb-6">
        {t.auth?.loginToAccess || 'Войдите в систему для доступа к этой функции'}
      </p>
      <div className="space-y-3 w-full max-w-48">
        <SignInButton mode="modal">
          <button className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            {t.signIn || 'Войти'}
          </button>
        </SignInButton>
        <SignUpButton mode="modal">
          <button className="w-full border border-purple-500 text-purple-300 hover:bg-purple-500/10 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            {t.signUp || 'Регистрация'}
          </button>
        </SignUpButton>
      </div>
    </div>
  );

  const renderContext = () => {
    // For unauthenticated users, show login prompt for protected contexts
    if (isUnauthenticated) {
      switch (activeContext) {
        case 'organization':
        case 'image':
        case 'gallery':
        case 'video':
        case 'audio':
        case 'text':
        case '3d':
        case 'multi':
        case 'admin':
          return <UnauthenticatedMessage />;
        
        case 'settings':
          return (
            <SettingsContext 
              selectedLanguage={selectedLanguage}
              onLanguageChange={onLanguageChange}
            />
          );
        
        case 'help':
          return (
            <HelpContext 
              selectedLanguage={selectedLanguage}
            />
          );
        
        default:
          return <UnauthenticatedMessage />;
      }
    }

    // For authenticated users, show normal contexts
    switch (activeContext) {
      case 'organization':
        return (
          <OrganizationContext 
            selectedLanguage={selectedLanguage}
          />
        );
      
      case 'image':
        return (
          <ImageGenerationContext
            selectedLanguage={selectedLanguage}
            onOpenTileSelector={onOpenTileSelector}
          />
        );

      case 'gallery':
        return (
          <GalleryFiltersContext 
            selectedLanguage={selectedLanguage}
            filters={galleryFilters || { limit: 24, offset: 0 }}
            onFiltersChange={onGalleryFiltersChange || (() => {})}
          />
        );
      
      case 'video':
        return (
          <ImageGenerationContext
            selectedLanguage={selectedLanguage}
            onOpenTileSelector={onOpenTileSelector}
          />
        );

      case 'audio':
        return (
          <ImageGenerationContext
            selectedLanguage={selectedLanguage}
            onOpenTileSelector={onOpenTileSelector}
          />
        );

      case 'text':
        return (
          <ImageGenerationContext
            selectedLanguage={selectedLanguage}
            onOpenTileSelector={onOpenTileSelector}
          />
        );

      // 3D uses the same panel as the other generation sections — this is the
      // ONLY mount site of ImageGenerationContext, so a missing case leaves the
      // section with no workflow selector and no Generate button (silent
      // `default: return null`), not an error.
      case '3d':
        return (
          <ImageGenerationContext
            selectedLanguage={selectedLanguage}
            onOpenTileSelector={onOpenTileSelector}
          />
        );

      case 'multi':
        // Model selection lives here, like the workflow selector on the classic
        // sections; the picker element itself comes from MultiGenerationPage.
        return (
          <MultiContext selectedLanguage={selectedLanguage} footer={multiSidebarFooter}>
            {multiSidebarPanel}
          </MultiContext>
        );

      case 'settings':
        return (
          <SettingsContext
            selectedLanguage={selectedLanguage}
            onLanguageChange={onLanguageChange}
          />
        );

      case 'help':
        return (
          <HelpContext 
            selectedLanguage={selectedLanguage}
          />
        );
      
      case 'admin':
        return (
          <AdminContext 
            selectedLanguage={selectedLanguage}
            onLanguageChange={onLanguageChange}
          />
        );
      
      default:
        return null;
    }
  };

  if (isContextPanelCollapsed) {
    return null;
  }

  return (
    <div className={`fixed left-20 top-0 h-full w-80 bg-gray-900/95 backdrop-blur-md border-r border-gray-700/50 z-40 flex flex-col transition-all duration-300 ${className}`}>
      <div className="flex-1 overflow-y-auto">
        {renderContext()}
      </div>
      
    </div>
  );
}